import React, { useEffect, useState } from 'react';
import { HiOutlineArrowDownTray, HiOutlineChartPie, HiOutlineLockClosed, HiOutlineTrophy, HiOutlineUserGroup, HiOutlineWallet } from 'react-icons/hi2';
import { adminApi } from '../services/api';
import DataTable from '../components/DataTable';
import { SettingsSkeleton } from '../components/Skeletons';
import { rankLabel, rankTitle } from '../utils/ranks';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function useGroupForm(group) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminApi
      .settings(group)
      .then((data) => {
        if (active) setForm(data.settings);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [group]);

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSave(event, extra) {
    event.preventDefault();
    setMsg('');
    setError('');
    setBusy(true);
    try {
      const payload = { ...form, ...(extra || {}) };
      delete payload.treasury_private_key_set;
      delete payload.treasury_address;
      delete payload.dfi_balance;
      delete payload.bnb_balance;
      delete payload.treasury_balance_error;
      delete payload.tokendesk_api_key_set;
      delete payload.bscscan_api_key_set;
      delete payload.rank_last_refresh_at;
      delete payload.rank_bonus_last_at;
      const data = await adminApi.updateSettings(group, payload);
      setForm(data.settings);
      setMsg('Settings saved');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { form, loading, msg, error, busy, setMsg, setError, setForm, update, onSave };
}

function Notes({ msg, error }) {
  return (
    <>
      {msg ? <p className="ok">{msg}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </>
  );
}

function FieldGrid({ form, update, fields }) {
  return (
    <div className="settings-grid">
      {fields.map(([key, label]) => (
        <label key={key}>
          {label}
          <input value={form[key] ?? ''} onChange={(event) => update(key, event.target.value)} />
        </label>
      ))}
    </div>
  );
}

function GroupPage({ group, children }) {
  const state = useGroupForm(group);
  if (state.error && !state.form) {
    return (
      <section>
        <p className="error">{state.error}</p>
      </section>
    );
  }
  if (state.loading || !state.form) return <SettingsSkeleton />;
  return children(state);
}

export function PlansSettings() {
  return (
    <GroupPage group="plans">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['plan_min_usdt', 'Plan min USDT'],
                ['plan_max_usdt', 'Plan max USDT'],
                ['plan_daily_max_usdt', 'Max plan deposit per user per day (USDT)'],
                ['plan_user_max_usdt', 'Max invest per user (USDT)'],
                ['plan_preset_amounts', 'Plan preset amounts (comma-separated, e.g. 1,10,50,100)'],
                ['daily_return_percent', 'Daily rate %'],
                ['earn_cap_multiplier', 'Earn cap for ranks below high-from (2 = 2x)'],
                ['earn_cap_high_from_rank', 'High earn-cap starts at rank (7 = ranks 7–10)'],
                ['earn_cap_high_multiplier', 'Earn cap from that rank upward (3 = 3x)'],
                ['claim_min_amount', 'Claim minimum'],
              ]}
            />
            <div className="settings-note">
              <h2>
                <HiOutlineArrowDownTray aria-hidden="true" />
                Claim charges
              </h2>
              <p className="hint">
                A percent of each wallet claim is held as a charge before DFI is sent. Each existing upline, up to the level count, receives that upline percent of the charge. Missing uplines are skipped and leftover charge is not redistributed. Example: 50 claimed at 10% is a 5 charge, and each of 10 uplines gets 10% of 5 = 0.5.
              </p>
            </div>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['claim_charge_percent', 'Claim charge %'],
                ['claim_charge_upline_percent', 'Each upline % of charge'],
                ['claim_charge_upline_levels', 'Upline levels'],
              ]}
            />
            <div className="settings-note">
              <h2>
                <HiOutlineWallet aria-hidden="true" />
                Registration fee
              </h2>
              <p className="hint">
                New wallets send this BNB amount to one admin address during registration. This is a single wallet, not a random pool. Set the fee to 0 to register without a payment.
              </p>
            </div>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['register_fee_bnb', 'Registration fee (BNB)'],
                ['register_wallet', 'Registration wallet'],
              ]}
            />
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function ContractSettings() {
  return (
    <GroupPage group="contracts">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['usdt_contract', 'USDT contract'],
                ['dfi_contract', 'DFI contract'],
                ['dfi_per_usdt', 'DFI per unit'],
                ['root_wallet', 'Root wallet'],
                ['chain_id', 'Chain ID'],
              ]}
            />
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function SupportSettings() {
  return (
    <GroupPage group="support">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['support_telegram', 'Support Telegram URL'],
                ['support_email', 'Support email'],
              ]}
            />
            <p className="hint">
              Use a full t.me link or a handle such as @username. Handles are saved as https://t.me/username.
            </p>
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

function toLocalInput(value) {
  const date = new Date(value || '');
  if (!value || Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function LaunchSettings() {
  return (
    <GroupPage group="launch">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <label>
              Launch date and time
              <input
                type="datetime-local"
                value={toLocalInput(form.launch_at)}
                onChange={(event) => {
                  const next = event.target.value;
                  update('launch_at', next ? new Date(next).toISOString() : '');
                }}
              />
            </label>
            <div className="actions">
              <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              <button
                type="button"
                className="ghost"
                disabled={busy || !form.launch_at}
                onClick={(event) => {
                  update('launch_at', '');
                  onSave(event, { launch_at: '' });
                }}
              >
                Clear
              </button>
            </div>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function GatewaySettings() {
  const [apiKeyInput, setApiKeyInput] = useState('');
  return (
    <GroupPage group="gateway">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form
            className="settings panel"
            onSubmit={async (event) => {
              const extra = {};
              if (apiKeyInput.trim()) extra.tokendesk_api_key = apiKeyInput.trim();
              const saved = await onSave(event, extra);
              if (saved) setApiKeyInput('');
            }}
          >
            <label>
              Token Desk URL
              <input
                value={form.tokendesk_api_url ?? ''}
                onChange={(event) => update('tokendesk_api_url', event.target.value)}
                placeholder="https://tokendesk.dynafi.xyz"
                autoComplete="off"
              />
            </label>
            <label>
              API key
              <input
                type="password"
                value={apiKeyInput}
                onChange={(event) => setApiKeyInput(event.target.value)}
                placeholder={
                  form.tokendesk_api_key_set
                    ? 'Key is set — leave blank to keep, or paste a new key'
                    : 'Paste the Token Desk API key'
                }
                autoComplete="off"
              />
              <span className="hint">
                {form.tokendesk_api_key_set
                  ? 'Stored in the database. Leave blank to keep the current key. Type “clear” to remove it.'
                  : 'Plan deposits use this key. It is not shown to users.'}
              </span>
            </label>
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

function formatCoin(value) {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export function TreasurySettings() {
  const [treasuryInput, setTreasuryInput] = useState('');
  return (
    <GroupPage group="treasury">
      {({ form, onSave, busy, msg, error }) => (
        <section>
          <div className="panel treasury-balances">
            {form.treasury_address ? (
              <>
                <p className="treasury-balances__address">{form.treasury_address}</p>
                <div className="treasury-balances__grid">
                  <div>
                    <span>DFI</span>
                    <strong>{formatCoin(form.dfi_balance)}</strong>
                  </div>
                  <div>
                    <span>BNB</span>
                    <strong>{formatCoin(form.bnb_balance)}</strong>
                  </div>
                </div>
                {form.treasury_balance_error ? <p className="error">{form.treasury_balance_error}</p> : null}
              </>
            ) : (
              <p className="hint">Treasury wallet is not set.</p>
            )}
          </div>
          <form
            className="settings panel"
            onSubmit={async (event) => {
              const extra = {};
              if (treasuryInput.trim()) extra.treasury_private_key = treasuryInput.trim();
              const saved = await onSave(event, extra);
              if (saved) setTreasuryInput('');
            }}
          >
            <label>
              Treasury private key
              <input
                type="password"
                value={treasuryInput}
                onChange={(event) => setTreasuryInput(event.target.value)}
                placeholder={
                  form.treasury_private_key_set
                    ? 'Key is set — leave blank to keep, or paste a new key'
                    : 'Paste treasury private key (0x…)'
                }
                autoComplete="off"
              />
              <span className="hint">
                {form.treasury_private_key_set
                  ? 'Stored in the database. Leave blank to keep the current key. Type “clear” to remove it.'
                  : 'Used to send DFI on claim. Stored in the database.'}
              </span>
            </label>
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function RankSettings() {
  const [refreshing, setRefreshing] = useState(false);
  const [paying, setPaying] = useState(false);
  return (
    <GroupPage group="ranks">
      {({ form, update, onSave, busy, msg, error, setMsg, setError, setForm }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <div className="settings-note">
              <h2>
                <HiOutlineTrophy aria-hidden="true" />
                Rank
              </h2>
              <p className="hint">
                Rank 0 is the default. Matching uses the smaller of the strong leg and the other legs. From the start rank through DynaFi Legend, each required direct team must include the ranked-user count at the minimum rank. Explorer means any rank. Crown Ambassador needs 3 different legs with Elite Leader unlocked, Global Ambassador needs 3 different Ambassador legs, and DynaFi Legend needs 3 different Crown Ambassador legs. A weekly bonus is paid only when matching business grew by the growth percent over the growth days. Otherwise that week is delayed. Last rank refresh:{' '}
                {form.rank_last_refresh_at ? new Date(form.rank_last_refresh_at).toLocaleString() : 'not yet'}.
                Last bonus run:{' '}
                {form.rank_bonus_last_at ? new Date(form.rank_bonus_last_at).toLocaleString() : 'not yet'}.
              </p>
            </div>
            <div className="settings-grid">
              <label>
                Rank refresh time (HH:MM)
                <input value={form.rank_refresh_time ?? ''} onChange={(event) => update('rank_refresh_time', event.target.value)} />
              </label>
              <label>
                Bonus weekday
                <select value={form.rank_bonus_weekday ?? '1'} onChange={(event) => update('rank_bonus_weekday', event.target.value)}>
                  {WEEKDAYS.map((name, index) => (
                    <option key={name} value={String(index + 1)}>{name}</option>
                  ))}
                </select>
              </label>
              <label>
                Bonus time (HH:MM)
                <input value={form.rank_bonus_time ?? ''} onChange={(event) => update('rank_bonus_time', event.target.value)} />
              </label>
              <label>
                Bonus growth percent
                <input value={form.rank_bonus_growth_percent ?? ''} onChange={(event) => update('rank_bonus_growth_percent', event.target.value)} />
              </label>
              <label>
                Bonus growth days
                <input value={form.rank_bonus_growth_days ?? ''} onChange={(event) => update('rank_bonus_growth_days', event.target.value)} />
              </label>
              <label>
                Team gate starts at rank
                <input value={form.rank_team_from_rank ?? ''} onChange={(event) => update('rank_team_from_rank', event.target.value)} />
              </label>
              <label>
                Direct teams required
                <input value={form.rank_team_legs ?? ''} onChange={(event) => update('rank_team_legs', event.target.value)} />
              </label>
              <label>
                Ranked users per team
                <input value={form.rank_team_users ?? ''} onChange={(event) => update('rank_team_users', event.target.value)} />
              </label>
              <label>
                Minimum rank in each team
                <input value={form.rank_team_min_rank ?? ''} onChange={(event) => update('rank_team_min_rank', event.target.value)} />
              </label>
            </div>
            <DataTable
              framed={false}
              className="rank-setup"
              searchPlaceholder="Search ranks"
              rowKey={(row) => row.rank}
              rows={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rank) => ({ rank }))}
              columns={[
                { key: 'rank', label: 'Rank', sort: (row) => row.rank, search: (row) => `${row.rank} ${rankTitle(row.rank)}`, render: (row) => rankLabel(row.rank) },
                {
                  key: 'match',
                  label: 'Matching USDT',
                  sort: (row) => form[`rank_${row.rank}_usdt`],
                  search: (row) => form[`rank_${row.rank}_usdt`],
                  render: (row) => (
                    <input value={form[`rank_${row.rank}_usdt`] ?? ''} onChange={(event) => update(`rank_${row.rank}_usdt`, event.target.value)} />
                  ),
                },
                {
                  key: 'weekly',
                  label: 'Weekly bonus',
                  sort: (row) => form[`rank_${row.rank}_weekly`],
                  search: (row) => form[`rank_${row.rank}_weekly`],
                  render: (row) => (
                    <input value={form[`rank_${row.rank}_weekly`] ?? ''} onChange={(event) => update(`rank_${row.rank}_weekly`, event.target.value)} />
                  ),
                },
                {
                  key: 'max',
                  label: 'Max reward',
                  sort: (row) => form[`rank_${row.rank}_max`],
                  search: (row) => form[`rank_${row.rank}_max`],
                  render: (row) => (
                    <input value={form[`rank_${row.rank}_max`] ?? ''} onChange={(event) => update(`rank_${row.rank}_max`, event.target.value)} />
                  ),
                },
              ]}
            />
            <div className="action-row">
              <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              <button
                type="button"
                className="ghost"
                disabled={refreshing}
                onClick={async () => {
                  setMsg('');
                  setError('');
                  setRefreshing(true);
                  try {
                    const data = await adminApi.refreshRanks();
                    setMsg(`Ranks updated for ${data.result?.users ?? 0} users`);
                    const fresh = await adminApi.settings('ranks');
                    setForm(fresh.settings);
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setRefreshing(false);
                  }
                }}
              >
                {refreshing ? 'Refreshing…' : 'Refresh ranks now'}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={paying}
                onClick={async () => {
                  setMsg('');
                  setError('');
                  setPaying(true);
                  try {
                    const data = await adminApi.payRankBonuses();
                    const result = data.result || {};
                    setMsg(
                      result.skipped
                        ? 'Rank bonuses were already paid today'
                        : `Paid ${result.payments || 0} rank bonus credits, total ${result.total || 0}. Delayed ${result.delayed || 0}.`
                    );
                    const fresh = await adminApi.settings('ranks');
                    setForm(fresh.settings);
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setPaying(false);
                  }
                }}
              >
                {paying ? 'Paying…' : 'Pay rank bonuses now'}
              </button>
            </div>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function ReferralSettings() {
  return (
    <GroupPage group="referrals">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <div className="settings-note">
              <h2>
                <HiOutlineUserGroup aria-hidden="true" />
                Referral leader pool
              </h2>
              <p className="hint">
                Top referrers are ranked by how much their direct users have deposited. At the end of each day this percent of that day’s confirmed deposits is split by the place percents. An empty place is not paid to anyone else.
                The dashboard Top deposits board uses a dummy yesterday total picked at 02:00 India time from the min/max range below, then shows each rank’s bonus from the same pool percents. Actual pool payouts still use confirmed deposits.
              </p>
            </div>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['referral_top_pool_percent', 'Pool percent of the day\'s deposits'],
                ['referral_top_1_percent', 'Rank 1 percent of pool'],
                ['referral_top_2_percent', 'Rank 2 percent of pool'],
                ['referral_top_3_percent', 'Rank 3 percent of pool'],
                ['referral_top_4_percent', 'Rank 4 percent of pool'],
                ['referral_top_5_percent', 'Rank 5 percent of pool'],
                ['display_business_min_usdt', 'Dashboard dummy business min (USDT)'],
                ['display_business_max_usdt', 'Dashboard dummy business max (USDT)'],
              ]}
            />
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

export function LevelSettings() {
  return (
    <GroupPage group="levels">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form className="settings panel" onSubmit={onSave}>
            <div className="settings-note">
              <h2>
                <HiOutlineChartPie aria-hidden="true" />
                ROI upline commission
              </h2>
              <p className="hint">
                Level 1 is open. A higher level opens only after every level below it. Second level means the directs of those directs. {rankTitle(form.roi_upline_extra_min_rank || 7)}+ adds the extra percent.
              </p>
            </div>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['direct_upline_commission_percent', 'Direct upline commission %'],
                ['roi_upline_extra_percent', 'Extra percent'],
                ['roi_upline_extra_min_rank', 'Minimum rank for extra'],
                ['commission_min_invest_usdt', 'Minimum self invest to receive commission'],
              ]}
            />
            <DataTable
              framed={false}
              className="rank-setup"
              searchPlaceholder="Search levels"
              rowKey={(row) => row.level}
              rows={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((level) => ({ level }))}
              columns={[
                { key: 'level', label: 'Level', sort: (row) => row.level, search: (row) => `Level ${row.level}`, render: (row) => `Level ${row.level}` },
                {
                  key: 'base',
                  label: 'Base %',
                  sort: (row) => form[`roi_level_${row.level}_percent`],
                  search: (row) => form[`roi_level_${row.level}_percent`],
                  render: (row) => (
                    <input value={form[`roi_level_${row.level}_percent`] ?? ''} onChange={(event) => update(`roi_level_${row.level}_percent`, event.target.value)} />
                  ),
                },
                {
                  key: 'boost',
                  label: `${rankTitle(form.roi_upline_extra_min_rank || 7)}+`,
                  sort: (row) => {
                    const base = Number(form[`roi_level_${row.level}_percent`]);
                    const extra = Number(form.roi_upline_extra_percent);
                    return Number.isFinite(base) && Number.isFinite(extra) ? base + extra : '';
                  },
                  render: (row) => {
                    const base = Number(form[`roi_level_${row.level}_percent`]);
                    const extra = Number(form.roi_upline_extra_percent);
                    return Number.isFinite(base) && Number.isFinite(extra) ? base + extra : '—';
                  },
                },
                {
                  key: 'gen1',
                  label: '1st level users',
                  sort: (row) => form[`roi_level_${row.level}_gen1_count`],
                  search: (row) => form[`roi_level_${row.level}_gen1_count`],
                  render: (row) => (
                    <input value={form[`roi_level_${row.level}_gen1_count`] ?? ''} onChange={(event) => update(`roi_level_${row.level}_gen1_count`, event.target.value)} />
                  ),
                },
                {
                  key: 'gen1min',
                  label: '1st level min',
                  sort: (row) => form[`roi_level_${row.level}_gen1_min`],
                  search: (row) => form[`roi_level_${row.level}_gen1_min`],
                  render: (row) => (
                    <input value={form[`roi_level_${row.level}_gen1_min`] ?? ''} onChange={(event) => update(`roi_level_${row.level}_gen1_min`, event.target.value)} />
                  ),
                },
                {
                  key: 'gen2',
                  label: '2nd level users',
                  sort: (row) => form[`roi_level_${row.level}_gen2_count`],
                  search: (row) => form[`roi_level_${row.level}_gen2_count`],
                  render: (row) => (
                    <input value={form[`roi_level_${row.level}_gen2_count`] ?? ''} onChange={(event) => update(`roi_level_${row.level}_gen2_count`, event.target.value)} />
                  ),
                },
                {
                  key: 'gen2min',
                  label: '2nd level min',
                  sort: (row) => form[`roi_level_${row.level}_gen2_min`],
                  search: (row) => form[`roi_level_${row.level}_gen2_min`],
                  render: (row) => (
                    <input value={form[`roi_level_${row.level}_gen2_min`] ?? ''} onChange={(event) => update(`roi_level_${row.level}_gen2_min`, event.target.value)} />
                  ),
                },
              ]}
            />
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
        </section>
      )}
    </GroupPage>
  );
}

// Decimal keypads in many locales type "," — keep one "." so Number() can read the rate
function decimalText(value) {
  const [whole, ...rest] = value.replace(/,/g, '.').replace(/[^\d.]/g, '').split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
}

function slabDraft(slab) {
  return { days: String(slab.days), return_percent: String(slab.return_percent), is_active: slab.is_active };
}

function StakingSlabs() {
  const [slabs, setSlabs] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('');
  const [percent, setPercent] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  async function load() {
    const data = await adminApi.stakingSlabs();
    const list = data.slabs || [];
    setSlabs(list);
    setDrafts(Object.fromEntries(list.map((slab) => [slab.id, slabDraft(slab)])));
  }

  useEffect(() => {
    load()
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  function edit(id, key, value) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }

  async function run(id, action, done) {
    setMsg('');
    setError('');
    setBusyId(id);
    try {
      await action();
      await load();
      setMsg(done);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function onAdd(event) {
    event.preventDefault();
    const ok = await run('new', () => adminApi.createStakingSlab({ days: Number(days), return_percent: Number(percent) }), 'Slab added');
    if (ok) {
      setDays('');
      setPercent('');
    }
  }

  function save(slab) {
    const draft = drafts[slab.id];
    return run(
      slab.id,
      () => adminApi.updateStakingSlab(slab.id, {
        days: Number(draft.days),
        return_percent: Number(draft.return_percent),
        is_active: draft.is_active,
      }),
      `${draft.days}-day slab saved`
    );
  }

  function remove(slab) {
    if (!window.confirm(`Delete the ${slab.days}-day slab? Running stakes keep their rate.`)) return;
    run(slab.id, () => adminApi.deleteStakingSlab(slab.id), 'Slab deleted');
  }

  return (
    <div className="settings panel">
      <div className="settings-note">
        <h2>
          <HiOutlineLockClosed aria-hidden="true" />
          Staking slabs
        </h2>
        <p className="hint">
          Each slab is a lock period and the return paid at the end of it. A stake keeps the days and rate it started with, so edits only affect new stakes. Inactive slabs are hidden from users.
        </p>
      </div>
      <form className="settings-grid" onSubmit={onAdd}>
        <label>
          Days
          <input value={days} onChange={(event) => setDays(event.target.value)} inputMode="numeric" placeholder="e.g. 60" required />
        </label>
        <label>
          Return %
          <input value={percent} onChange={(event) => setPercent(decimalText(event.target.value))} inputMode="decimal" placeholder="e.g. 5" required />
        </label>
        <button type="submit" disabled={busyId === 'new'} style={{ alignSelf: 'end' }}>
          {busyId === 'new' ? 'Adding…' : 'Add slab'}
        </button>
      </form>
      <DataTable
        framed={false}
        className="rank-setup"
        loading={loading}
        rows={slabs}
        empty="No slabs yet — users cannot stake until you add one"
        searchPlaceholder="Search slabs"
        columns={[
          {
            key: 'days',
            label: 'Days',
            sort: (row) => row.days,
            render: (row) => (
              <input value={drafts[row.id]?.days ?? ''} onChange={(event) => edit(row.id, 'days', event.target.value)} inputMode="numeric" />
            ),
          },
          {
            key: 'return_percent',
            label: 'Return %',
            sort: (row) => row.return_percent,
            render: (row) => (
              <input value={drafts[row.id]?.return_percent ?? ''} onChange={(event) => edit(row.id, 'return_percent', decimalText(event.target.value))} inputMode="decimal" />
            ),
          },
          {
            key: 'example',
            label: '100 USDT returns',
            render: (row) => {
              const rate = Number(drafts[row.id]?.return_percent);
              return Number.isFinite(rate) ? `${(100 + rate).toLocaleString(undefined, { maximumFractionDigits: 4 })} USDT` : '—';
            },
          },
          {
            key: 'is_active',
            label: 'Status',
            sort: (row) => (row.is_active ? 'Active' : 'Inactive'),
            search: (row) => (row.is_active ? 'Active' : 'Inactive'),
            render: (row) => (
              <select
                value={drafts[row.id]?.is_active ? '1' : '0'}
                onChange={(event) => edit(row.id, 'is_active', event.target.value === '1')}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            ),
          },
          {
            key: 'created_at',
            label: 'Created',
            sort: (row) => row.created_at || '',
            render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '—'),
          },
          {
            key: 'updated_at',
            label: 'Updated',
            sort: (row) => row.updated_at || '',
            render: (row) => (row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'),
          },
          {
            key: 'actions',
            label: 'Actions',
            className: 'actions',
            render: (row) => (
              <>
                <button type="button" className="ghost" disabled={busyId === row.id} onClick={() => save(row)}>
                  {busyId === row.id ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="ghost danger" disabled={busyId === row.id} onClick={() => remove(row)}>
                  Delete
                </button>
              </>
            ),
          },
        ]}
      />
      <Notes msg={msg} error={error} />
    </div>
  );
}

export function StakingSettings() {
  const [apiKeyInput, setApiKeyInput] = useState('');
  return (
    <GroupPage group="staking">
      {({ form, update, onSave, busy, msg, error }) => (
        <section>
          <form
            className="settings panel"
            onSubmit={async (event) => {
              const extra = {};
              if (apiKeyInput.trim()) extra.bscscan_api_key = apiKeyInput.trim();
              const saved = await onSave(event, extra);
              if (saved) setApiKeyInput('');
            }}
          >
            <div className="settings-note">
              <h2>
                <HiOutlineLockClosed aria-hidden="true" />
                Stake amount
              </h2>
              <p className="hint">
                Users stake DFI. Limits apply to a single stake. Staking does not count as plan investment: it pays no commissions, daily ROI, or rank business, and its payout is not limited by the earn cap.
              </p>
            </div>
            <FieldGrid
              form={form}
              update={update}
              fields={[
                ['stake_min_dfi', 'Min stake (DFI)'],
                ['stake_max_dfi', 'Max stake (DFI)'],
              ]}
            />
            <div className="settings-note">
              <h2>
                <HiOutlineLockClosed aria-hidden="true" />
                Deposit addresses
              </h2>
              <p className="hint">
                Each new stake is shown one of these wallets at random, and the user sends their DFI there. Matured stakes are paid back from the treasury wallet, so keep it funded with DFI and BNB.
              </p>
            </div>
            <label>
              DFI deposit addresses (one per line)
              <textarea
                rows={4}
                value={form.stake_deposit_addresses ?? ''}
                onChange={(event) => update('stake_deposit_addresses', event.target.value)}
                placeholder="0x…"
                spellCheck={false}
              />
              <span className="hint">Users cannot stake until at least one address is set.</span>
            </label>
            <div className="settings-note">
              <h2>
                <HiOutlineLockClosed aria-hidden="true" />
                BscScan verification
              </h2>
              <p className="hint">
                Every transfer is checked on BscScan before its stake starts. BscScan is served through the Etherscan V2 API, so use an Etherscan API key with BNB Smart Chain access.
              </p>
            </div>
            <label>
              API URL
              <input
                value={form.bscscan_api_url ?? ''}
                onChange={(event) => update('bscscan_api_url', event.target.value)}
                placeholder="https://api.etherscan.io/v2/api"
                autoComplete="off"
              />
            </label>
            <label>
              API key
              <input
                type="password"
                value={apiKeyInput}
                onChange={(event) => setApiKeyInput(event.target.value)}
                placeholder={
                  form.bscscan_api_key_set
                    ? 'Key is set — leave blank to keep, or paste a new key'
                    : 'Paste the BscScan / Etherscan API key'
                }
                autoComplete="off"
              />
              <span className="hint">
                {form.bscscan_api_key_set
                  ? 'Stored in the database. Leave blank to keep the current key. Type “clear” to remove it.'
                  : 'Stakes stay pending until a key is set.'}
              </span>
            </label>
            <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </form>
          <Notes msg={msg} error={error} />
          <StakingSlabs />
        </section>
      )}
    </GroupPage>
  );
}
